// @ts-check
// appearance.js — "Appearance" settings section (theme picker).
//
// Extracted from settings-panel.js (Stage B of the monolith split). The whole
// picker lives in the heading's caret dropdown (shared `openMenu`): a single
// non-expandable header row whose switcher opens a menu of all themes, each item
// filled with its own theme background and its own derived body-text colour
// (`--text`) so every theme reads as itself and the label is always legible.
// Owns the persistence side of applying a theme (_applyThemeById).
//
// The global repaint subscriber (onThemeChange → mermaid/graph/preview/panel)
// stays in the host: it coordinates a full panel re-render and isn't specific
// to rendering this section. renderAppearance(ctx) uses ctx.row, ctx.rawRow,
// ctx.openExpansions.

import {
  applyThemeById,
  deriveThemeVars,
  getCurrentTheme,
  getNamedThemes,
  NAMED_THEMES,
  syncThemeToNote,
} from '../../ui/theme-engine.js';
import { openMenu, toggleMenu } from '../../ui/menu-popover.js';
import type { SettingsSectionCtx } from '../settings-section-api.js';
import { Settings } from '../settings-store.js';

// Search text shared between the collapsed row and the swatch strip so
// typing any theme name in the settings search finds Appearance. Derived
// from NAMED_THEMES so renaming or adding a theme can't drift this list.
const _themeNamesSearchText = NAMED_THEMES.map((t) => t.name.toLowerCase()).join(' ');

// Resolved theme gallery + each theme's OWN derived body-text colour (`--text`),
// computed ONCE at load: themes are static and deriveThemeVars is heavy (~30
// HSL/RGB conversions each), so the dropdown never re-derives them on open.
const _THEMES: any[] = getNamedThemes();
const _THEME_TEXT = new Map<string, string>(
  _THEMES.map((t) => [t.id, deriveThemeVars(t.background, t.accent)['--text']])
);

/**
 * @param {any} ctx - the section render context.
 * @returns {any[]} the section's rows.
 */
export function renderAppearance(ctx: SettingsSectionCtx) {
  const ID = 'group:appearance';

  // The whole picker lives in the dropdown — the section is a single header row
  // whose right-hand control is the theme SWITCHER (active theme name + caret).
  // No expandable body: clicking the switcher opens the menu, where every theme
  // is shown filled with its own background colour. The host re-renders this
  // section on every theme repaint, so the button text stays current.
  return [
    ctx.row({
      id: ID,
      label: 'Appearance',
      control: buildThemeSwitcher,
      updateControl: refreshThemeSwitcher,
      expandable: false,
      cls: 'settings-row-group',
      searchText: `appearance theme background accent colour color ${_themeNamesSearchText}`,
    }),
  ];
}

// The Appearance heading's right-hand control: the active theme name as a caret
// dropdown button that opens a menu of ALL themes to switch to — the same
// affordance (and shared `openMenu`) as the Workspaces switcher / Sync account
// menu. Each menu item is filled with its own theme background and the theme's
// own derived text colour, so the themes are easy to tell apart and every label
// stays legible. Reads the theme engine fresh on click so it's never a stale
// closure.
function buildThemeSwitcher(): HTMLElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'settings-value-btn settings-value-btn-menu';
  refreshThemeSwitcher(btn);
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    toggleMenu(btn, () => {
      const activeId = getCurrentTheme().themeId;
      openMenu(
        btn,
        _THEMES.map((t) => ({
          label: t.name,
          color: t.accent,
          // The theme's own background + its OWN derived body-text colour, so
          // each item reads exactly as that theme would and the label is
          // guaranteed legible for every theme.
          background: t.background,
          textColor: _THEME_TEXT.get(t.id),
          active: t.id === activeId,
          onSelect: () =>
            _applyThemeById(t.id).catch((err) =>
              console.warn('[settings] apply theme failed:', err)
            ),
        }))
      );
    });
  });
  return btn;
}

function refreshThemeSwitcher(el: HTMLElement): void {
  const btn = el as HTMLButtonElement;
  const curName = NAMED_THEMES.find((t) => t.id === getCurrentTheme().themeId)?.name || '–';
  btn.textContent = curName;
  btn.title = 'Switch theme';
  btn.setAttribute('aria-haspopup', 'menu');
  btn.setAttribute('aria-label', `Theme ${curName}; switch`);
}

/**
 * @param {any} id - the theme id to apply + persist.
 * @returns {Promise<void>}
 */
async function _applyThemeById(id: any) {
  // applyThemeById mutates CSS variables and fires onThemeChange — the
  // subscription registered in the host picks up the repaint chain (mermaid,
  // graph, preview, panel re-render) for both same-tab and cross-tab
  // changes. This function only handles the persistence concerns that
  // shouldn't run on a passive cross-tab broadcast.
  applyThemeById(id);
  const t = getCurrentTheme();
  if (Settings) {
    // Await all three sets in parallel so a rapid follow-up click can't
    // race a half-flushed previous write. Promise.all rejection bubbles to
    // the caller's catch instead of becoming an unhandled rejection.
    await Promise.all([
      Settings.set('appearance.themeId', t.themeId),
      Settings.set('appearance.background', t.background),
      Settings.set('appearance.accent', t.accent),
    ]);
  }
  // Keep the legacy theme-engine prefs-note schema (theme + themeTs)
  // in sync too, so other devices' applySyncedPreferences can pick up
  // the change. Runs after Settings.set so the read-merge-write doesn't
  // race the appearance field. (Storage write — must not throw out of the
  // click handler.)
  try {
    await syncThemeToNote();
  } catch (e: any) {
    console.warn('[settings] syncThemeToNote failed:', e);
  }
}
